import {
  EasyCallControlFactory,
  IApi,
  IDevice,
  ILogEvent,
  init,
  ISingleCallControl,
  ISingleInitialState,
  IWebHidDevice,
  RequestedBrowserTransport,
  webHidPairing,
} from '@gnaudio/jabra-js';
import { config } from 'lib-shared-backend-config';
import { Sentry } from 'lib-shared-sentry';

/**
 * Configuration for Jabra, see docs:
 * https://developer.jabra.com/site/global/sdk/javascript/api-reference/iconfig.gsp
 */
const jabraConfig = {
  partnerKey: config.sdk.jabra.partnerKey,
  appId: config.sdk.jabra.appId,
  appName: config.sdk.jabra.appName,
  transport: RequestedBrowserTransport.WEB_HID,
  logger: {
    write(logEvent: ILogEvent) {
      if (logEvent.level === 'error') {
        Sentry.captureException(logEvent.message);

        /** Keeping around for debugging while experimental */
        console.error('Jabra error', logEvent.message, logEvent.layer);
      }
    },
  },
};

type Subscription = ReturnType<IApi['deviceAdded']['subscribe']>;

export type JabraEventType = keyof JabraEvent;

export type JabraDeviceError = { message: string };

/** Represents the events */
export type JabraEvent = {
  /** When the device changes call state */
  readonly callActive: (callActive: boolean) => void;
  /** When the Jabra SDK is ready */
  readonly ready: () => void;
  /** When the device has signalled incoming call */
  readonly hasIncomingCall: (hasIncomingCall: boolean) => void;
  /** When there's change to the devices list (fires on ready) */
  readonly devicesList: (devices: IDevice[]) => void;
  /** When there's added devices */
  readonly deviceAdded: (device: IDevice) => void;
  /** When a device is removed */
  readonly deviceRemoved: (device: IDevice) => void;
  /** When the device has accepted an incoming call */
  readonly callAccepted: () => void;
  /** When the device has rejected an incoming call */
  readonly callRejected: () => void;
  /** When the device hangs up */
  readonly hangup: () => void;
  /** When there is a device registered and set to use */
  readonly device: (device: IDevice) => void;
  /** When the device in use has been changed */
  readonly deviceChange: (device: IDevice) => void;
  /** When there's an error message */
  readonly error: (error: JabraDeviceError) => void;
};

/** Represents the event listeners */
export type JabraEventListeners = {
  /** The event listeners that will be called */
  readonly [TJabraEvent in keyof JabraEvent]: JabraEvent[TJabraEvent][];
};

/** Utility class that interacts with Jabra devices */
class JabraDeviceManager {
  public jabraSDK?: IApi;
  public jabraEasyControlFactory?: EasyCallControlFactory;
  public isInActiveCall = false;
  public isBusy = false;
  public hasLock = false;
  private _connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private _preventEmitHangup = false;
  private _devices: IDevice[] = [];
  private _callControlSubscriptions: Subscription[] = [];
  private _callControl?: ISingleCallControl;
  private _eventListeners: JabraEventListeners = {
    callActive: [],
    ready: [],
    hasIncomingCall: [],
    devicesList: [],
    device: [],
    deviceAdded: [],
    deviceRemoved: [],
    callAccepted: [],
    callRejected: [],
    hangup: [],
    deviceChange: [],
    error: [],
  };
  private _deviceSubscriptions: Subscription[] = [];

  /** Gets the currently active call control */
  public get callControl(): ISingleCallControl | undefined {
    return this._callControl;
  }

  /** Gets the currently active device */
  public get currentDevice(): IDevice | undefined {
    return this.callControl?.device;
  }

  /** Gets all attached and initialized devices */
  public get devices(): IDevice[] {
    return this._devices;
  }

  /** Change which device should be controlled / used */
  public async changeDevice(device: IDevice): Promise<void> {
    if (this.callControl) {
      this._removeCallControl();
    }

    await this._addCallControl(device);

    this._emitEvent(this._eventListeners.deviceChange, device);

    return;
  }

  /** Change device using the serial number */
  public async changeDeviceBySerialNumber(serialNumber: string): Promise<void> {
    const device = this._devices.find(
      (_device) => _device.serialNumber === serialNumber,
    );

    if (device) {
      this.changeDevice(device);
    }

    return;
  }

  /** Request the browsers built-in setup dialog for web HID devices */
  public async setupDevice(): Promise<IWebHidDevice[]> {
    return await webHidPairing();
  }

  /** Set the current device state to indicate it is in an active call */
  public async startCall(): Promise<void> {
    return this.callControl?.startCall();
  }

  /** Set the current device state to indicate it is not in an active call */
  public async endCall(
    {
      emitHangup,
    }: {
      /** If it should emit event hangup */
      emitHangup: boolean;
    } = { emitHangup: true },
  ): Promise<void> {
    if (emitHangup === false) {
      this._preventEmitHangup = true;
    }

    if (this.isInActiveCall) {
      this.callControl?.endCall();
    }

    return;
  }

  /** Set the active device into a ringing state */
  public async signalIncomingCall(
    ringTimeout?: number,
  ): Promise<boolean | undefined> {
    if (!this.callControl) {
      return;
    }

    this.isBusy = true;

    try {
      this._emitEvent(this._eventListeners.hasIncomingCall, true);

      const wasAccepted =
        await this.callControl?.signalIncomingCall(ringTimeout);

      if (wasAccepted) {
        this._emitEvent(this._eventListeners.callAccepted);
      } else {
        this.isBusy = false;
        this._emitEvent(this._eventListeners.callRejected);
      }

      this._emitEvent(this._eventListeners.hasIncomingCall, false);

      return wasAccepted;
    } catch (err) {
      /** Keeping around for debugging while experimental */
      console.warn('signalIncomingError', err);
      return;
    }
  }

  /** Reject incoming call and set the active device into default state */
  public async rejectIncomingCall(): Promise<void> {
    return this.callControl?.rejectIncomingCall();
  }

  /** Accept incoming call and set the device to active call state */
  public async acceptIncomingCall(): Promise<void> {
    return this.callControl?.acceptIncomingCall();
  }

  /** Attach an event listener */
  public addEventListener(
    eventType: JabraEventType,
    eventListener: JabraEvent[JabraEventType],
  ): void {
    (this._eventListeners[eventType] as JabraEvent[JabraEventType][]).push(
      eventListener,
    );
  }

  /** Remove an event listener */
  public removeEventListener(
    eventType: JabraEventType,
    eventListener: JabraEvent[JabraEventType],
  ): void {
    (this._eventListeners[eventType] as JabraEvent[JabraEventType][]) = (
      this._eventListeners[eventType] as JabraEvent[JabraEventType][]
    ).filter((registeredListener) => registeredListener !== eventListener);
  }

  /** Initialize Jabra and set up event listeners for device management */
  async init(): Promise<boolean> {
    if (this.jabraSDK) {
      return false;
    }

    try {
      this.jabraSDK = await init(jabraConfig);

      this._emitEvent(this._eventListeners.ready);

      this.jabraEasyControlFactory = new EasyCallControlFactory(this.jabraSDK);

      this._deviceSubscriptions = [
        this.jabraSDK.deviceAdded.subscribe(async (device) => {
          await this._addCallControl(device);

          this._emitEvent(this._eventListeners.deviceAdded, device);
        }),
        this.jabraSDK.deviceList.subscribe(async (devices) => {
          this._devices = devices;
          this._emitEvent(this._eventListeners.devicesList, devices);
        }),
        this.jabraSDK.deviceRemoved.subscribe(async (device) => {
          this._emitEvent(this._eventListeners.deviceRemoved, device);

          if (this._devices.length) {
            this.changeDevice(this._devices[this._devices.length - 1]);
          }
        }),
      ];

      return true;
    } catch (err) {
      this._emitEvent(this._eventListeners.error, {
        message: 'Failed to connect to device',
      });

      /** Keeping around for debugging while experimental */
      console.error('init', err);
      return false;
    }
  }

  async deinit(): Promise<void> {
    if (!this.jabraSDK) {
      return;
    }

    this._deviceSubscriptions.forEach((s) => s.unsubscribe());
    await this._removeCallControl();
    this._callControlSubscriptions.forEach((s) => s.unsubscribe());
    this.jabraSDK = undefined;
  }

  /**
   * Create and add a call control lock for device and force it into default
   * state
   */
  private _addCallControl = async (
    device: IDevice,
    initialState?: ISingleInitialState | undefined,
  ): Promise<void> => {
    if (!this.jabraEasyControlFactory?.supportsEasyCallControl(device)) {
      return;
    }

    try {
      this._callControl =
        await this.jabraEasyControlFactory.createSingleCallControl(
          device,
          initialState ?? { callActive: false },
        );

      this._callControlSubscriptions = [
        this._callControl.callActive.subscribe(this._handleCallActive),
        this._callControl.onDisconnect.subscribe(() =>
          this._removeCallControl(),
        ),
      ];

      this._emitEvent(this._eventListeners.device, device);
      this._emitEvent(this._eventListeners.deviceChange, device);

      this.hasLock = true;
    } catch (err) {
      /** Keeping around for debugging while experimental */
      console.warn('addCallControl', err);
    }
  };

  /**
   * Delete call control for device and set any remaining devices as current
   * device
   */
  private _removeCallControl = async (): Promise<void> => {
    this.hasLock = false;

    try {
      await this._callControl?.endCall();
    } catch (error) {
      /** Keeping around for debugging while experimental */
      console.warn('endCall', error);
    }

    try {
      this._callControl?.teardown();
      /** Force continued execution to next render cycle */
      await Promise.resolve();
    } catch (error) {
      /** Keeping around for debugging while experimental */
      console.warn('teardown', error);
    }

    this._callControlSubscriptions.forEach((subscription) =>
      subscription.unsubscribe(),
    );
  };

  /** Emit events for active state */
  private _handleCallActive = (active: boolean): void => {
    if (!active && this.isInActiveCall && !this._preventEmitHangup) {
      this._emitEvent(this._eventListeners.hangup);
    }

    this._preventEmitHangup = false;

    this.isInActiveCall = active;
    this.isBusy = active;
    this._emitEvent(this._eventListeners.callActive, active);
  };

  /** Call attached event listener callback function */
  private _emitEvent(
    eventListeners: JabraEvent[JabraEventType][],
    value?: JabraEvent[JabraEventType]['arguments'],
  ) {
    eventListeners.forEach((listener) => listener(value));
  }

  public async reconnect(initialState?: ISingleInitialState | undefined) {
    if (this._connectionTimeout) clearTimeout(this._connectionTimeout);

    this._connectionTimeout = setTimeout(async () => {
      const device = this.currentDevice;

      if (!device || this.hasLock || this.isBusy) {
        return;
      }

      this._addCallControl(device, initialState);
    }, 5000);

    return;
  }

  public async disconnect() {
    if (this._connectionTimeout) clearTimeout(this._connectionTimeout);

    if (this.isBusy) {
      return;
    }

    if (!this.hasLock) {
      return;
    }

    this._removeCallControl();
  }
}

/** Pre-created singleton instance */
export const jabraDeviceManager: JabraDeviceManager = new JabraDeviceManager();
